// Wires Clerk's getToken() into a per-request fetch wrapper, then exposes
// the resulting client via context. We rebuild the client only when the
// getToken closure identity changes (sign-in / sign-out), so all live
// react-query subscribers re-render together rather than racing.

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { type ApiClient, createApiClient } from './api-client.js';
import { env } from '../env.js';
import { ThemeProvider } from './theme.js';
import { SidebarStateProvider } from './sidebar-state.js';
import { TooltipProvider } from '../components/ui/tooltip.js';
import { Toaster } from '../components/ui/toaster.js';

// Exported for tests: smoke tests inject a fake client without going
// through Clerk's <ClerkProvider>. Production code should use
// useApiClient(); the context export is not part of the public surface.
export const ApiClientContext = createContext<ApiClient | null>(null);

export function useApiClient(): ApiClient {
  const client = useContext(ApiClientContext);
  if (!client) {
    throw new Error('useApiClient must be used inside <AppProviders>');
  }
  return client;
}

function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: (failureCount, error) => {
          // 401/403 → don't retry; the user needs to re-auth or lacks access.
          const status = (error as { status?: number }).status;
          if (status === 401 || status === 403) return false;
          return failureCount < 2;
        },
        refetchOnWindowFocus: false,
      },
    },
  });
}

export function AppProviders({ children }: { children: ReactNode }) {
  const { getToken } = useAuth();

  const apiClient = useMemo(
    () =>
      createApiClient({
        baseUrl: env.apiBaseUrl,
        getToken: () => getToken(),
      }),
    [getToken],
  );

  // QueryClient survives across renders; rebuilding it would dump cache.
  const queryClient = useMemo(makeQueryClient, []);

  return (
    <ThemeProvider>
      <SidebarStateProvider>
        <QueryClientProvider client={queryClient}>
          <ApiClientContext.Provider value={apiClient}>
            <TooltipProvider delayDuration={200}>
              {children}
              <Toaster />
            </TooltipProvider>
          </ApiClientContext.Provider>
        </QueryClientProvider>
      </SidebarStateProvider>
    </ThemeProvider>
  );
}
